import { CloudUpload } from 'lucide-react';
import HeaderSearch from './Search';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ApiServices from 'src/shared/Services/apiService';
import { useRole } from 'src/contexts/RoleContext/RoleContext';
import { useThemeMode } from '../../../../theme/ThemeProvider';
import { motion } from 'framer-motion';

function HeaderButtons() {
  const navigate = useNavigate();
  const { role } = useRole();
  const { isDark } = useThemeMode();
  const [docCount, setDocCount] = useState<number | null>(null);

  useEffect(() => {
    if (role !== 'patient') return;
    let mounted = true;
    const loadCount = async () => {
      try {
        const res = await ApiServices.getMedicalDocsCount();
        if (mounted) setDocCount(res?.counts?.total);
      } catch { /* silent */ }
    };
    loadCount();
    return () => { mounted = false; };
  }, [role]);

  useEffect(() => {
    const handleUpdateDocCount = (event: CustomEvent) => {
      setDocCount(event.detail);
    };
    window.addEventListener('updateDocCount', handleUpdateDocCount as EventListener);
    return () => {
      window.removeEventListener('updateDocCount', handleUpdateDocCount as EventListener);
    };
  }, []);

  return (
    <div className="flex items-center gap-0.5">
      <HeaderSearch />

      {role === 'patient' && (
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/patient-details/UploadDocuments')}
          className={[
            // Hidden on the narrowest phones: it is a shortcut to a page the
            // profile tabs already reach, and the row has no room to spare —
            // keeping it pushed the user avatar off the right edge.
            'hidden sm:flex',
            'relative p-2 rounded-xl min-h-[40px] min-w-[40px]',
            'items-center justify-center',
            'transition-all duration-200 group',
            isDark
              ? 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.07]'
              : 'text-slate-400 hover:text-brand-500 hover:bg-brand-50/70',
          ].join(' ')}
          aria-label="Upload Documents"
          title="Upload Documents"
        >
          <CloudUpload
            size={17}
            strokeWidth={1.9}
            className="transition-transform duration-200 group-hover:scale-105 group-hover:-translate-y-px"
          />
          {docCount != null && docCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[17px] h-[17px] px-1 text-micro-3xs font-bold text-white rounded-full ring-2 ring-white dark:ring-surface-dark animate-scale-in bg-gradient-to-br from-brand-400 to-brand-500 shadow-dark-brand-glow">
              {docCount}
            </span>
          )}
        </motion.button>
      )}
    </div>
  );
}

export default HeaderButtons;
